import { AgentStreamHttpError } from "./agentStream";
import type { AgentMemory, WireToolCall } from "./agentMemory";
import type { StreamHandlers, StreamOutcome } from "./agentStream";
import type { CallResult, StepOutcome } from "./callHandlers";
import { listLink, resultForModel } from "./listResults";
import { cutAtFabricatedTurn } from "./fabricatedTurns";
import type { ListLink, StatusLabel } from "./listResults";
import { emptyTurnProgress } from "./stopSummary";
import type { TurnProgress } from "./stopSummary";
import type { ToolResult } from "./tools";

// Failures surface to onPlanError in the shape useApiError's toast path understands, so the user
// reads the translated error code instead of a generic "something went wrong".
const shapedPlanError = (status: number, errorCode: string | null, fallback: unknown): unknown =>
  errorCode ? { status, data: { errorCode } } : fallback;

const parseArgs = (argsJson: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(argsJson || "{}");
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

export type TurnOutcome = StepOutcome | "answered";

export type TurnResult = {
  outcome: TurnOutcome;
  plannerReply: string | null;
  toolContext: string[];
  usedToolNames: string[];
  lists: ListLink[];
  cutOff?: boolean;
  endedBy?: TurnEnd;
};

export type TurnEnd = "stepLimit" | "sizeLimit" | "stopped";

export const STOPPED_MARK = "[Brukeren stoppet svaret her, så teksten over er bare delvis skrevet.]";

export type TurnLoopDeps = {
  planStream: (handlers: StreamHandlers) => Promise<StreamOutcome>;
  executeCall: (callName: string, args: Record<string, unknown>, plannerSummary: string | null) => Promise<CallResult>;
  memory: AgentMemory;
  maxSteps: number;
  setActiveStep: (label: string | null) => void;
  onPlannerText: (accumulated: string) => void;
  onToolCallStart: (name: string) => void;
  onPlanError: (err: unknown) => void;
  statusLabel?: StatusLabel;
  shouldStop?: () => boolean;
  fitsNextPlan?: () => boolean;
  progress?: TurnProgress;
  onFabricatedTurn?: (droppedToolCalls: string[]) => void;
  cardCorrection?: (answer: string) => string | null;
};

// Drive the plan/execute loop for one user turn over streamed plans. A tool-less round IS the
// answer: its text has already streamed to the turn via onPlannerText, so no separate answer phase
// runs (outcome "answered"). Rounds with tool calls execute every call through the existing gate;
// a call may end the turn early (a confirmed mutation or a cancel), delegating the
// SKIPPED balancing to memory — the answer phase then synthesizes from the tool results ("stream").
// The assistant tool-call message enters memory only from a fully assembled stream outcome, so a
// dropped stream can never leave a dangling call.
export const runTurnLoop = async (deps: TurnLoopDeps): Promise<TurnResult> => {
  const { planStream, executeCall, memory, maxSteps, setActiveStep, onPlannerText, onToolCallStart, onPlanError } =
    deps;
  const toolContext: string[] = [];
  const usedToolNames: string[] = [];
  const lists: ListLink[] = [];
  let outcome: TurnOutcome = "stream";
  let plannerReply: string | null = null;
  const progress = deps.progress ?? emptyTurnProgress();
  const skipFrom = (toolCalls: WireToolCall[], fromIndex: number) => {
    memory.pushSkippedRemainder(toolCalls, fromIndex);
    progress.notStarted.push(...toolCalls.slice(fromIndex).map((call) => call.name));
  };
  const stopped = () => deps.shouldStop?.() === true;
  let askedForCard = false;
  let cardRetry: { answer: string; note: string } | null = null;
  const keepFirstAnswer = (retry: { answer: string; note: string }): TurnResult => {
    memory.withdrawTrailingUser(retry.note);
    onPlannerText(retry.answer);
    return { outcome: "answered", plannerReply: retry.answer, toolContext, usedToolNames, lists };
  };
  const stoppedWhileWriting = (written: string, announced: string[]): TurnResult => {
    progress.notStarted.push(...announced);
    memory.pushAssistantReply(`${written}\n\n${STOPPED_MARK}`);
    return { outcome: "answered", plannerReply: written, toolContext, usedToolNames, lists, endedBy: "stopped" };
  };
  const stoppedResult = (): TurnResult => ({
    outcome: "stream",
    plannerReply,
    toolContext,
    usedToolNames,
    lists,
    endedBy: "stopped",
  });

  for (let step = 0; step < maxSteps; step++) {
    if (cardRetry && (stopped() || deps.fitsNextPlan?.() === false)) return keepFirstAnswer(cardRetry);
    if (stopped()) return stoppedResult();
    if (deps.fitsNextPlan?.() === false) {
      return { outcome: "stream", plannerReply, toolContext, usedToolNames, lists, endedBy: "sizeLimit" };
    }
    setActiveStep("Tenker");
    let planned: StreamOutcome;
    let written = "";
    const announced: string[] = [];
    try {
      planned = await planStream({
        onDelta: (accumulated) => {
          written = cutAtFabricatedTurn(accumulated).text;
          onPlannerText(written);
        },
        onToolCallStart: (name) => {
          announced.push(name);
          onToolCallStart(name);
        },
      });
    } catch (planErr) {
      if (cardRetry) return keepFirstAnswer(cardRetry);
      if (stopped() && written.trim() && !announced.length) return stoppedWhileWriting(written, []);
      if (stopped()) {
        progress.notStarted.push(...announced);
        return stoppedResult();
      }
      const shaped = planErr instanceof AgentStreamHttpError
        ? shapedPlanError(planErr.status, planErr.errorCode, planErr)
        : planErr;
      onPlanError(shaped);
      return { outcome: "aborted", plannerReply, toolContext, usedToolNames, lists };
    }
    const said = cutAtFabricatedTurn(planned.text);
    const toolCalls = said.fabricated ? [] : planned.toolCalls;
    if (said.fabricated) deps.onFabricatedTurn?.(planned.toolCalls.map((call) => call.name));
    if (cardRetry && !toolCalls.length && (planned.errored || stopped() || !said.text.trim())) {
      return keepFirstAnswer(cardRetry);
    }
    cardRetry = null;
    if (stopped() && planned.errored && !toolCalls.length && said.text.trim()) return stoppedWhileWriting(said.text, announced);
    if (stopped() && (planned.errored || toolCalls.length > 0)) {
      progress.notStarted.push(...(toolCalls.length ? toolCalls.map((call) => call.name) : announced));
      return stoppedResult();
    }
    if (planned.errored && planned.toolCalls.length === 0 && !planned.text.trim()) {
      onPlanError(shapedPlanError(503, planned.errorCode, new Error("plan stream delivered nothing")));
      return { outcome: "aborted", plannerReply, toolContext, usedToolNames, lists };
    }
    if (!toolCalls.length) {
      plannerReply = said.text.trim() ? said.text : null;
      if (planned.errored) {
        return { outcome: "answered", plannerReply, toolContext, usedToolNames, lists, cutOff: true };
      }
      if (plannerReply !== null) {
        memory.pushAssistantReply(plannerReply);
      }
      const correction = !askedForCard && plannerReply !== null ? (deps.cardCorrection?.(plannerReply) ?? null) : null;
      if (correction !== null && plannerReply !== null) {
        askedForCard = true;
        cardRetry = { answer: plannerReply, note: correction };
        memory.pushUser(correction);
        continue;
      }
      return { outcome: "answered", plannerReply, toolContext, usedToolNames, lists };
    }

    memory.pushAssistantWithToolCalls(planned.text.trim() ? planned.text : null, toolCalls);
    const plannerSummary = planned.text.trim() ? planned.text : null;
    let breakOuter = false;
    for (let i = 0; i < toolCalls.length; i++) {
      if (stopped()) {
        skipFrom(toolCalls, i);
        return stoppedResult();
      }
      const call = toolCalls[i];
      const args = parseArgs(call.arguments);
      const callResult = await executeCall(call.name, args, plannerSummary);
      const forModel = resultForModel(call.name, callResult.result, deps.statusLabel);
      memory.pushToolResult(call.id, forModel, call.name);
      progress.done.push({ name: call.name, result: callResult.result });
      if (describesStoredState(call.name, forModel)) dropEarlierReads(toolContext);
      toolContext.push(`${call.name}: ${JSON.stringify(forModel)}`);
      const link = listLink(call.name, args, callResult.result);
      if (link) {
        lists.push(link);
      }
      usedToolNames.push(call.name);
      if (callResult.outcome !== null) {
        outcome = callResult.outcome;
      }
      if (callResult.breakOuter) {
        skipFrom(toolCalls, i + 1);
        breakOuter = true;
        break;
      }
    }
    if (breakOuter) {
      return { outcome, plannerReply, toolContext, usedToolNames, lists };
    }
  }

  return { outcome, plannerReply, toolContext, usedToolNames, lists, endedBy: "stepLimit" };
};

const describesStoredState = (callName: string, result: ToolResult): boolean =>
  result.ok
    ? callName === "updateSourcingEvent"
    : result.current !== undefined || result.cardFieldsNow !== undefined;

const READS_OF_A_PROCUREMENT = /^(getSourcingEvent|updateSourcingEvent): /;

const dropEarlierReads = (toolContext: string[]): void => {
  const kept = toolContext.filter((entry) => !READS_OF_A_PROCUREMENT.test(entry));
  toolContext.splice(0, toolContext.length, ...kept);
};
